import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

const COMMENT_MAX_LENGTH = 2000;

export default function InitiativeComment({
  comment,
  canEdit,
  canDelete,
  onEditComment,
  onDeleteComment,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const startEditing = () => {
    setIsEditing(true);
    setEditDraft(comment.text);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditDraft("");
  };

  const handleSaveEdit = async () => {
    const text = editDraft.trim();
    if (!text) return;
    setIsSavingEdit(true);
    const result = await onEditComment(comment.id, text);
    setIsSavingEdit(false);
    if (result) cancelEditing();
  };

  const styles = {
    commentBox: {
      p: 1.5,
      borderRadius: 1.5,
      bgcolor: "background.tableHeader",
    },
    commentHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
    },
    commentMeta: {
      color: "background.textMuted",
      display: "block",
      mb: 0.5,
    },
    commentActions: { flexShrink: 0 },
    commentActionBtn: { p: 0.25 },
    commentActionIcon: { fontSize: 16 },
    commentText: { overflowWrap: "anywhere" },
    editRow: { display: "flex", flexDirection: "column", gap: 1 },
    editActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    editButtons: { display: "flex", gap: 1 },
    editButton: { textTransform: "none" },
    maxLength: { htmlInput: { maxLength: COMMENT_MAX_LENGTH } },
  };

  return (
    <Box sx={styles.commentBox}>
      <Box sx={styles.commentHeader}>
        <Typography variant="caption" sx={styles.commentMeta}>
          {comment.author} ·{" "}
          {new Date(comment.date).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
          {comment.editedAt &&
            ` · edited ${new Date(comment.editedAt).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}`}
        </Typography>
        {!isEditing && (canEdit || canDelete) && (
          <Stack direction="row" sx={styles.commentActions}>
            {canEdit && (
              <IconButton
                size="small"
                onClick={startEditing}
                sx={styles.commentActionBtn}
              >
                <EditIcon sx={styles.commentActionIcon} />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                size="small"
                onClick={() => onDeleteComment(comment.id)}
                sx={styles.commentActionBtn}
              >
                <DeleteIcon sx={styles.commentActionIcon} />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>
      {isEditing ? (
        <Box sx={styles.editRow}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            disabled={isSavingEdit}
            slotProps={styles.maxLength}
          />
          <Box sx={styles.editActions}>
            <Typography variant="caption">
              {editDraft.length >= COMMENT_MAX_LENGTH
                ? `Character limit reached (${COMMENT_MAX_LENGTH})`
                : ""}
            </Typography>
            <Box sx={styles.editButtons}>
              <Button
                size="small"
                onClick={cancelEditing}
                disabled={isSavingEdit}
                sx={styles.editButton}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveEdit}
                disabled={isSavingEdit || !editDraft.trim()}
                sx={styles.editButton}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={styles.commentText}>
          {comment.text}
        </Typography>
      )}
    </Box>
  );
}
